import { useGetDayjsLocalizedCalendarLabels, useLocalizationContext } from '@va/localization';
import { TimeInterval } from '@va/types/time-series';
import { inWebsiteTimezone } from '@va/util/helpers';
import dayjs from 'dayjs';
import { isNil } from 'lodash';
import { ReactNode, useCallback, useMemo } from 'react';
import { IndicatorLinesArgs, TooltipHeaderArgs } from './ExternalChartTooltip';
import { chartIncludesToday } from './chartIntervals';
import { LimitReachedLine } from './common/LimitReachedLine';
import { LimitReachedTooltipHeader } from './common/LimitReachedTooltipHeader';

export const useLimitReachedChartRenderers = ({
  limitReachedTimestamps,
  unit = 'day',
  isCurrentlyLimitReached = false,
}: {
  limitReachedTimestamps: number[];
  unit?: dayjs.OpUnitType;
  isCurrentlyLimitReached?: boolean;
}) => {
  const { translate, locale } = useLocalizationContext();
  const calendarLabels = useGetDayjsLocalizedCalendarLabels();

  const timestampDayjs = useMemo(
    () => limitReachedTimestamps.map((ts) => ({ ts, day: inWebsiteTimezone(ts) })),
    [limitReachedTimestamps],
  );

  const matchIntervals = useCallback(
    (currentTimeIntervals: TimeInterval[]) =>
      timestampDayjs
        .map(({ ts, day }) => ({
          ts,
          index: currentTimeIntervals.findIndex(({ from }) => day.isSame(inWebsiteTimezone(from), unit)),
        }))
        .filter(({ index }) => index >= 0),
    [timestampDayjs, unit],
  );

  const renderLimitLines = useCallback(
    ({ chart, currentTimeIntervals, offsetLeft, lastDataIndex }: IndicatorLinesArgs): ReactNode => {
      const liveOccupiesLastIndex = !isCurrentlyLimitReached && chartIncludesToday(currentTimeIntervals);
      const lines = matchIntervals(currentTimeIntervals).flatMap(({ index }) => {
        if (liveOccupiesLastIndex && index === lastDataIndex) return [];
        const x = chart.scales['x']?.getPixelForValue(index);
        return isNil(x) || Number.isNaN(x) ? [] : [{ index, left: offsetLeft + x }];
      });

      if (lines.length === 0) return null;

      return (
        <>
          {lines.map(({ index, left }) => (
            <LimitReachedLine
              key={index}
              chart={chart}
              dataIndex={index}
              left={left}
              label={translate('dashboard.lineChart.limitReachedChip')}
            />
          ))}
        </>
      );
    },
    [isCurrentlyLimitReached, matchIntervals, translate],
  );

  const renderLimitHeader = useCallback(
    ({ currentTimeIntervals, dataPoints }: TooltipHeaderArgs): ReactNode => {
      const currentDataIndex = dataPoints?.[0]?.dataIndex ?? -1;
      const matched = matchIntervals(currentTimeIntervals).find(({ index }) => index === currentDataIndex);

      if (!matched) return null;

      return (
        <LimitReachedTooltipHeader
          chipLabel={translate('dashboard.lineChart.tooltip.limitReachedTitle', {
            date: inWebsiteTimezone(matched.ts).locale(locale).calendar(null, calendarLabels),
          })}
        />
      );
    },
    [calendarLabels, matchIntervals, locale, translate],
  );

  return { renderLimitLines, renderLimitHeader };
};
